import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { STATUS_LIST, STATUS_LABEL, STATUS_STYLES } from "@/lib/constants";
import { useAuth, useCap } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import {
  ClipboardList,
  Activity,
  CheckCircle2,
  AlertTriangle,
  Package,
  Building2,
  TrendingUp,
  Wallet,
  Clock,
  Share2,
  ClipboardCopy,
} from "lucide-react";
import { toast } from "sonner";
import { buildPendingOrdersReport } from "@/lib/pendingReport";
import { copyToClipboard } from "@/lib/waMessages";

function ymNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmt(n) {
  return n == null ? "—" : `$${Number(n).toFixed(2)}`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finance, setFinance] = useState(null);

  useEffect(() => {
    api
      .get("/stats/dashboard")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // Solo admin: cargar resumen financiero del mes en curso.
    // Pedimos only=kpis para descargar sólo los sub-objetos que el Dashboard renderiza
    // (evita ~256 KB de tablas de detalle que se usan sólo en /admin/reports).
    if (user?.role !== "admin") return;
    const month = ymNow();
    Promise.all([
      api.get(`/admin/reports/production?month=${month}&only=kpis`).catch(() => null),
      api.get(`/admin/reports/payments?month=${month}&only=kpis`).catch(() => null),
      api.get(`/admin/reports/pending-pickup?only=kpis`).catch(() => null),
    ]).then(([prod, pay, pend]) => {
      setFinance({
        prod: prod?.data?.kpis || null,
        pay: pay?.data?.kpis || null,
        pending: pend?.data?.kpis || null,
      });
    });
  }, [user]);

  if (loading) {
    return <div className="text-slate-500 font-mono">Cargando dashboard…</div>;
  }
  if (!stats) return null;

  const kpis = [
    { label: "Activas", value: stats.totals.active, icon: Activity, color: "cyan", testid: "kpi-active" },
    { label: "Recibidas hoy", value: stats.by_status.RECEIVED, icon: ClipboardList, color: "sky", testid: "kpi-received" },
    { label: "En reparación", value: stats.by_status.IN_REPAIR, icon: Package, color: "violet", testid: "kpi-in-repair" },
    { label: "Listas", value: stats.by_status.READY, icon: CheckCircle2, color: "emerald", testid: "kpi-ready" },
    { label: "Retiradas", value: stats.totals.delivered, icon: CheckCircle2, color: "slate", testid: "kpi-delivered" },
    { label: "Atrasadas (7d+)", value: stats.totals.overdue, icon: AlertTriangle, color: "rose", testid: "kpi-overdue" },
  ];

  const colorMap = {
    cyan: "from-cyan-50 to-white border-cyan-200 text-cyan-700",
    sky: "from-sky-50 to-white border-sky-200 text-sky-700",
    violet: "from-blue-50 to-white border-blue-200 text-blue-700",
    emerald: "from-green-50 to-white border-green-200 text-green-700",
    slate: "from-slate-50 to-white border-slate-200 text-slate-700",
    rose: "from-red-50 to-white border-red-200 text-red-700",
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight" data-testid="dashboard-title">
            Panel General
          </h1>
          <p className="text-slate-500 text-sm mt-1">Estado en tiempo real del taller y sus sucursales.</p>
        </div>
        <PendingReportActions />
      </div>

      {/* Admin-only: Producción y cobros del mes en curso */}
      {user?.role === "admin" && finance && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="admin-finance-summary">
          <Link
            to="/admin/reports"
            className="card-base p-5 hover:border-blue-400 transition-all block"
            data-testid="finance-production"
          >
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={18} className="text-blue-600" />
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Producción del mes</div>
            </div>
            <div className="font-heading font-extrabold text-3xl text-slate-900">{finance.prod?.count ?? 0} <span className="text-sm font-normal text-slate-500">reparaciones</span></div>
            <div className="mt-2 flex gap-4 text-sm">
              <div><span className="text-slate-500">Venta</span> <b className="font-mono text-emerald-700">{fmt(finance.prod?.sale_total)}</b></div>
              <div><span className="text-slate-500">Ganancia</span> <b className="font-mono text-blue-700">{fmt(finance.prod?.profit_total)}</b></div>
            </div>
            {finance.prod?.missing_cost_count > 0 && (
              <div className="mt-2 text-xs text-amber-700">
                {finance.prod.missing_cost_count} sin costo configurado
              </div>
            )}
          </Link>
          <Link
            to="/admin/reports"
            className="card-base p-5 hover:border-emerald-400 transition-all block"
            data-testid="finance-payments"
          >
            <div className="flex items-center gap-2 mb-1">
              <Wallet size={18} className="text-emerald-600" />
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Dinero cobrado este mes</div>
            </div>
            <div className="font-heading font-extrabold text-3xl text-emerald-700 font-mono">{fmt(finance.pay?.paid_amount_total)}</div>
            <div className="mt-2 text-sm text-slate-500">{finance.pay?.count ?? 0} retiros / cobros registrados</div>
          </Link>
          <Link
            to="/admin/reports"
            className="card-base p-5 hover:border-amber-400 transition-all block"
            data-testid="finance-pending"
          >
            <div className="flex items-center gap-2 mb-1">
              <Clock size={18} className="text-amber-600" />
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Pendiente de retiro / cobro</div>
            </div>
            <div className="font-heading font-extrabold text-3xl text-amber-700">{finance.pending?.count ?? 0} <span className="text-sm font-normal text-slate-500">reparaciones</span></div>
            <div className="mt-2 text-sm text-slate-500">Venta pendiente: <b className="font-mono text-slate-800">{fmt(finance.pending?.sale_total)}</b></div>
          </Link>
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <div
            key={k.label}
            data-testid={k.testid}
            className={`kpi-card bg-gradient-to-br ${colorMap[k.color]}`}
          >
            <div className="flex items-center justify-between">
              <k.icon size={18} className="opacity-80" />
              <span className="text-[9px] font-mono uppercase tracking-widest opacity-70">{k.label}</span>
            </div>
            <div className="text-3xl font-heading font-extrabold mt-3">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Status distribution */}
      <div className="card-base p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl">Estado de las órdenes</h2>
          <Link to="/orders" className="text-sm text-blue-600 hover:text-blue-700">Ver todas →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {STATUS_LIST.map((st) => {
            const count = stats.by_status[st] || 0;
            return (
              <Link
                key={st}
                to={`/orders?status=${st}`}
                className="flex items-center justify-between bg-slate-50 hover:bg-white border border-slate-200 hover:border-blue-400 rounded-xl px-3 py-3 transition-all"
                data-testid={`status-card-${st}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 border ${STATUS_STYLES[st]}`}></span>
                  <span className="text-sm truncate">{STATUS_LABEL[st]}</span>
                </div>
                <span className="font-mono font-bold text-lg">{count}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* By branch */}
      <div className="card-base p-5">
        <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
          <Building2 size={20} className="text-blue-600" /> Órdenes activas por sucursal
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {stats.by_branch.length === 0 && (
            <div className="text-slate-500 text-sm col-span-3">No hay sucursales cargadas.</div>
          )}
          {stats.by_branch.map((b) => (
            <div key={b.branch_id} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 flex items-center justify-between">
              <div>
                <div className="font-semibold">{b.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">activas</div>
              </div>
              <div className="text-3xl font-heading font-extrabold text-blue-700">{b.active}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PendingReportActions() {
  const canView = useCap("orders.view");
  const [loading, setLoading] = useState(false);

  if (!canView) return null;

  async function fetchReport() {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/pending-orders");
      return data;
    } finally {
      setLoading(false);
    }
  }

  async function handleShare() {
    try {
      const data = await fetchReport();
      const text = buildPendingOrdersReport(data);
      // wa.me sin número → WhatsApp abre el selector de contacto/grupo.
      const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiError?.(e) || "No se pudo generar el reporte");
    }
  }

  async function handleCopy() {
    try {
      const data = await fetchReport();
      const text = buildPendingOrdersReport(data);
      const ok = await copyToClipboard(text);
      if (ok) toast.success(`Reporte copiado (${data.total_pending} pendientes)`);
      else toast.error("No se pudo copiar al portapapeles");
    } catch (e) {
      toast.error(formatApiError?.(e) || "No se pudo generar el reporte");
    }
  }

  return (
    <div className="flex flex-wrap gap-2" data-testid="pending-report-actions">
      <button
        type="button"
        onClick={handleShare}
        disabled={loading}
        className="btn-primary bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60"
        data-testid="share-pending-whatsapp"
        title="Compartir por WhatsApp"
      >
        <Share2 size={16} /> 📲 Compartir pendientes por WhatsApp
      </button>
      <button
        type="button"
        onClick={handleCopy}
        disabled={loading}
        className="btn-secondary disabled:opacity-60"
        data-testid="copy-pending-report"
        title="Copiar reporte al portapapeles"
      >
        <ClipboardCopy size={16} /> 📋 Copiar reporte
      </button>
    </div>
  );
}
